<template>
  <div>
    <nav class="hidden md:flex mb-4 h-8" aria-label="Breadcrumb">
      <ol role="list" class="flex space-x-4 rounded-md bg-white px-6 shadow">
        <li class="flex">
          <div class="flex items-center">
            <router-link to="/hub" class="transition ease-in-out duration-300 text-gray-400 hover:text-gray-500">
              <HomeIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span class="sr-only">Home</span>
            </router-link>
          </div>
        </li>
        <li class="flex">
          <div class="flex items-center">
            <svg class="h-full w-6 flex-shrink-0 text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
            </svg>
            <router-link to="/dashboard/routes" class="transition ease-in-out duration-300 ml-4 text-xs font-medium text-gray-500 hover:text-gray-700" aria-current="page">Menu tras</router-link>
          </div>
        </li>
        <li class="flex">
          <div class="flex items-center">
            <svg class="h-full w-6 flex-shrink-0 text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
            </svg>
            <div class="ml-4 text-xs font-medium text-gray-400 cursor-default" aria-current="page">Dev panel tras</div>
          </div>
        </li>
      </ol>
    </nav>

    <div class="mb-4 flex flex-col gap-3 bg-white p-4 shadow xl:flex-row xl:items-end xl:justify-between">
      <div>
        <h1 class="text-base font-semibold text-gray-900">Dev panel tras</h1>
        <p class="mt-1 text-sm text-gray-500">Panel admina do kontroli tablic i awaryjnego cofania statusu zakończonych tras.</p>
      </div>
      <div class="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div>
          <label for="route-created-date" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Data utworzenia</label>
          <input
            id="route-created-date"
            v-model="filters.createdDate"
            type="date"
            class="block min-h-[40px] w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:w-48"
          />
        </div>
        <button
          type="button"
          :disabled="loading || !filters.createdDate"
          @click.prevent="loadData"
          :class="[loading || !filters.createdDate ? 'cursor-default opacity-50' : 'hover:bg-indigo-200 active:scale-[0.96]', 'inline-flex min-h-[40px] items-center justify-center gap-2 bg-indigo-100 px-4 py-2 text-xs font-semibold text-indigo-700 transition-transform duration-150']"
        >
          <RefreshIcon class="h-4 w-4" aria-hidden="true" />
          Filtruj
        </button>
      </div>
    </div>

    <div v-if="notification.show" :class="[notification.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800', 'mb-4 p-4 text-sm shadow']">
      <p class="font-semibold">{{ notification.head }}</p>
      <p class="mt-1">{{ notification.subheader }}</p>
    </div>

    <div v-if="loading" class="bg-white p-6 text-sm text-gray-500 shadow">
      Ładowanie tras utworzonych dnia {{ filters.createdDate }} i snapshotów zamówień...
    </div>

    <div v-else class="space-y-4">
      <section v-for="group in groupedRoutes" :key="group.id" class="bg-white shadow">
        <div class="flex items-center justify-between bg-gray-50 px-4 py-3">
          <div>
            <h2 class="text-xs font-bold uppercase tracking-wide text-gray-500">{{ group.name }}</h2>
            <p class="mt-1 text-sm text-gray-500">{{ group.routes.length }} {{ customSizeOfRoutes(group.routes.length) }}</p>
          </div>
          <span :class="[statusBadgeClass(group.id), 'inline-flex items-center px-2.5 py-1 text-xs font-semibold']">
            status {{ group.id }}
          </span>
        </div>

        <div v-if="group.routes.length === 0" class="px-4 py-6 text-sm text-gray-400">
          Brak tras w tym statusie.
        </div>

        <div v-for="route in group.routes" :key="route.id" class="border-t border-gray-200 px-4 py-4">
          <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="text-sm font-semibold text-gray-900">{{ route.title || route.name || route.id }}</h3>
                <button type="button" @click.prevent="copyToClipboard(route.id)" class="text-xs font-medium text-indigo-600 hover:text-indigo-800">{{ route.id }}</button>
              </div>
              <p class="mt-1 text-xs text-gray-500">{{ formatRouteDate(route) }} · utworzono: {{ formatCreatedDate(route) }} · {{ route.type || 'brak typu' }}</p>
              <div class="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2 lg:grid-cols-4">
                <div class="bg-gray-50 px-3 py-2">
                  <span class="block font-semibold text-gray-900">{{ route.debug.orderRefs.length }}</span>
                  orders[]
                </div>
                <div class="bg-gray-50 px-3 py-2">
                  <span class="block font-semibold text-gray-900">{{ route.debug.orderIds.length }}</span>
                  orderIds[]
                </div>
                <div class="bg-gray-50 px-3 py-2">
                  <span class="block font-semibold text-gray-900">{{ route.debug.productIds.length }}</span>
                  productIds[]
                </div>
                <div :class="[route.debug.hasDiff ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700', 'px-3 py-2']">
                  <span class="block font-semibold">{{ route.debug.hasDiff ? 'Do sprawdzenia' : 'OK' }}</span>
                  spójność tablic
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-2 xl:justify-end">
              <button
                v-if="route.statusId === 3"
                type="button"
                :disabled="processingRouteId === route.id"
                @click.prevent="changeRouteStatus(route, 2)"
                :class="[processingRouteId === route.id ? 'cursor-default opacity-50' : 'hover:bg-blue-200 active:scale-[0.96]', 'inline-flex min-h-[40px] items-center justify-center bg-blue-100 px-3 py-2 text-xs font-semibold text-blue-700 transition-transform duration-150']"
              >
                Przywróć jako w trakcie
              </button>
              <button
                v-if="route.statusId === 3"
                type="button"
                :disabled="processingRouteId === route.id"
                @click.prevent="changeRouteStatus(route, 1)"
                :class="[processingRouteId === route.id ? 'cursor-default opacity-50' : 'hover:bg-yellow-200 active:scale-[0.96]', 'inline-flex min-h-[40px] items-center justify-center bg-yellow-100 px-3 py-2 text-xs font-semibold text-yellow-800 transition-transform duration-150']"
              >
                Przywróć jako zaplanowana
              </button>
              <button
                type="button"
                @click.prevent="toggleExpanded(route.id)"
                class="inline-flex min-h-[40px] items-center justify-center bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 transition-transform duration-150 hover:bg-gray-200 active:scale-[0.96]"
              >
                {{ expandedRoutes[route.id] ? 'Ukryj arraye' : 'Pokaż arraye' }}
              </button>
            </div>
          </div>

          <div v-if="route.debug.hasDiff" class="mt-3 bg-red-50 p-3 text-xs text-red-700">
            <p v-if="route.debug.missingInOrderIds.length">Brakuje w orderIds[]: {{ route.debug.missingInOrderIds.join(', ') }}</p>
            <p v-if="route.debug.missingInOrdersArray.length">Brakuje w orders[]: {{ route.debug.missingInOrdersArray.join(', ') }}</p>
            <p v-if="route.debug.missingProductIds.length">Brakuje w productIds[]: {{ route.debug.missingProductIds.join(', ') }}</p>
            <p v-if="route.debug.extraProductIds.length">Nadmiarowe productIds[]: {{ route.debug.extraProductIds.join(', ') }}</p>
          </div>

          <div v-if="expandedRoutes[route.id]" class="mt-4 grid gap-3 xl:grid-cols-2">
            <div class="bg-gray-900 p-3 text-xs text-gray-100">
              <p class="mb-2 font-semibold text-gray-300">orders[]</p>
              <pre class="max-h-80 overflow-auto whitespace-pre-wrap break-words">{{ stringify(route.debug.rawOrders) }}</pre>
            </div>
            <div class="bg-gray-900 p-3 text-xs text-gray-100">
              <p class="mb-2 font-semibold text-gray-300">orderIds[] / productIds[]</p>
              <pre class="max-h-80 overflow-auto whitespace-pre-wrap break-words">{{ stringify({ orderIds: route.debug.orderIds, productIds: route.debug.productIds }) }}</pre>
            </div>
            <div class="bg-gray-900 p-3 text-xs text-gray-100 xl:col-span-2">
              <p class="mb-2 font-semibold text-gray-300">snapshoty zamówień</p>
              <pre class="max-h-96 overflow-auto whitespace-pre-wrap break-words">{{ stringify(route.debug.orderSnapshots) }}</pre>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { db } from "@/firebase/gfbconf.js";
import { HomeIcon } from '@heroicons/vue/solid';
import { RefreshIcon } from '@heroicons/vue/outline';

export default {
  name: "RoutesDevPanel",
  components: {
    HomeIcon,
    RefreshIcon,
  },
  data() {
    return {
      loading: true,
      processingRouteId: null,
      routes: [],
      statuses: [],
      expandedRoutes: {},
      filters: {
        createdDate: this.getTodayDateInputValue(),
      },
      notification: {
        show: false,
        success: true,
        head: "",
        subheader: "",
      },
    }
  },
  computed: {
    isAdmin() {
      return this.$store.state.userData?.permissions?.admin === true;
    },
    isLocalhost() {
      return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
    },
    groupedRoutes() {
      const statuses = this.statuses.length > 0
        ? this.statuses
        : [
          { id: 1, name: "Zaplanowane" },
          { id: 2, name: "W trakcie" },
          { id: 3, name: "Zakończone" },
        ];

      const groups = statuses.map((status) => ({
        id: status.id,
        name: status.name || `Status ${status.id}`,
        routes: this.routes.filter((route) => route.statusId === status.id),
      }));

      const knownStatusIds = new Set(statuses.map((status) => status.id));
      const unknownRoutes = this.routes.filter((route) => !knownStatusIds.has(route.statusId));
      if(unknownRoutes.length > 0)
      {
        groups.push({
          id: "unknown",
          name: "Inne statusy",
          routes: unknownRoutes,
        });
      }

      return groups;
    },
  },
  async created() {
    if(!this.isAdmin || !this.isLocalhost)
    {
      this.$router.push("/dashboard/routes");
      return;
    }

    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await this.getStatuses();
        if(!this.filters.createdDate) throw "Wybierz datę utworzenia trasy.";

        const { start, end } = this.getSelectedDayRange(this.filters.createdDate);
        const routesSnapshot = await db.collection("Routes")
          .where("meta.createdDate", ">=", start)
          .where("meta.createdDate", "<=", end)
          .orderBy("meta.createdDate", "desc")
          .get();
        const routeDocs = [];
        routesSnapshot.forEach((doc) => {
          routeDocs.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        const preparedRoutes = [];
        for(const route of routeDocs)
        {
          preparedRoutes.push(await this.prepareRoute(route));
        }
        this.routes = preparedRoutes.sort((a, b) => this.getRouteDateUnix(b) - this.getRouteDateUnix(a));
      } catch (error) {
        console.log(error);
        this.setNotification(false, "Nie udało się załadować panelu.", "Sprawdź konsolę lub połączenie z Firestore.");
      } finally {
        this.loading = false;
      }
    },
    async getStatuses() {
      const res = await db.collection("RouteStatuses").doc("StatusList").get();
      this.statuses = Array.isArray(res.data()?.list) ? res.data().list : [];
    },
    async prepareRoute(route) {
      const orderRefs = Array.isArray(route.orders)
        ? route.orders.map((routeOrder) => ({
          index: routeOrder?.index || null,
          id: this.getRouteOrderId(routeOrder),
          path: routeOrder?.ref?.path || null,
          dateConfirmationSent: routeOrder?.dateConfirmationSent ?? null,
        })).filter((routeOrder) => routeOrder.id)
        : [];
      const orderIds = Array.isArray(route.orderIds) ? route.orderIds.filter(Boolean) : [];
      const allOrderIds = this.uniqueValues([...orderRefs.map((routeOrder) => routeOrder.id), ...orderIds]);
      const orderSnapshots = await this.getOrderSnapshots(allOrderIds);

      const routeProductIds = Array.isArray(route.productIds) ? route.productIds.filter(Boolean) : [];
      const productIdsFromOrders = this.uniqueValues(orderSnapshots.flatMap((order) => order.productIds || []));
      const missingInOrderIds = orderRefs.map((order) => order.id).filter((orderId) => !orderIds.includes(orderId));
      const missingInOrdersArray = orderIds.filter((orderId) => !orderRefs.some((order) => order.id === orderId));
      const missingProductIds = productIdsFromOrders.filter((productId) => !routeProductIds.includes(productId));
      const extraProductIds = routeProductIds.filter((productId) => !productIdsFromOrders.includes(productId));

      return {
        ...route,
        debug: {
          rawOrders: route.orders || [],
          orderRefs,
          orderIds,
          productIds: routeProductIds,
          productIdsFromOrders,
          missingInOrderIds,
          missingInOrdersArray,
          missingProductIds,
          extraProductIds,
          hasDiff: missingInOrderIds.length > 0 || missingInOrdersArray.length > 0 || missingProductIds.length > 0 || extraProductIds.length > 0,
          orderSnapshots,
        },
      };
    },
    getRouteOrderId(routeOrder) {
      if(routeOrder?.ref?.id) return routeOrder.ref.id;
      if(typeof routeOrder?.id === "string") return routeOrder.id;
      if(typeof routeOrder === "string") return routeOrder;
      return null;
    },
    getProductIdsFromOrder(order = {}) {
      return Object.values(order.positions || {})
        .map((position) => position?.product?.id)
        .filter((productId) => typeof productId === "string" && productId.length > 0);
    },
    uniqueValues(values = []) {
      return [...new Set(values.filter(Boolean))];
    },
    async getOrderSnapshots(orderIds = []) {
      const snapshots = [];
      const chunkSize = 10;

      for(let i = 0; i < orderIds.length; i += chunkSize)
      {
        const chunk = orderIds.slice(i, i + chunkSize);
        const chunkSnapshots = await Promise.all(chunk.map((orderId) => this.getOrderSnapshot(orderId)));
        snapshots.push(...chunkSnapshots);
      }

      return snapshots;
    },
    async getOrderSnapshot(orderId) {
      const orderDoc = await db.collection("Orders").doc(orderId).get();
      if(!orderDoc.exists)
      {
        return {
          id: orderId,
          exists: false,
        };
      }

      const order = orderDoc.data();
      const packages = Array.isArray(order?.packages) ? order.packages : [];
      return {
        id: orderId,
        exists: true,
        statusId: order?.statusId || null,
        isInRoute: order?.isInRoute ?? null,
        confirmed: order?.delivery?.date?.confirmed || null,
        packagesCount: packages.length,
        scannedPackagesCount: packages.filter((parcel) => parcel?.scannedInStorage === true).length,
        allPackagesScanned: packages.length > 0 && packages.every((parcel) => parcel?.scannedInStorage === true),
        productIds: this.getProductIdsFromOrder(order),
      };
    },
    getSelectedDayRange(dateValue) {
      const start = new Date(`${dateValue}T00:00:00`);
      const end = new Date(`${dateValue}T23:59:59.999`);
      return { start, end };
    },
    getTodayDateInputValue() {
      const now = new Date();
      const offset = now.getTimezoneOffset();
      const localDate = new Date(now.getTime() - (offset * 60 * 1000));
      return localDate.toISOString().slice(0, 10);
    },
    async changeRouteStatus(route, statusId) {
      const statusText = statusId === 2 ? "w trakcie" : "zaplanowana";
      const confirmed = window.confirm(`Przywrócić trasę "${route.title || route.name || route.id}" jako ${statusText}? Zamówienia zostaną przestawione zgodnie z paczkami.`);
      if(!confirmed) return;

      this.processingRouteId = route.id;
      try {
        const res = await axios.post(`${this.$store.state.apiLink}/route/dev/status`, {
          routeId: route.id,
          statusId,
          meta: this.$store.state.userData,
        });

        if(res.data?.success)
        {
          const updatedCount = res.data?.result?.updatedOrders?.length || 0;
          this.setNotification(true, "Status trasy przywrócony.", `Zaktualizowano ${updatedCount} zamówień.`);
          await this.loadData();
        }
        else
        {
          this.setNotification(false, "Nie udało się przywrócić trasy.", res.data?.log || "Backend zwrócił błąd.");
        }
      } catch (error) {
        console.log(error);
        this.setNotification(false, "Nie udało się przywrócić trasy.", "Sprawdź konsolę lub logi funkcji.");
      } finally {
        this.processingRouteId = null;
      }
    },
    toggleExpanded(routeId) {
      this.expandedRoutes[routeId] = !this.expandedRoutes[routeId];
    },
    setNotification(success, head, subheader) {
      this.notification = {
        show: true,
        success,
        head,
        subheader,
      };
    },
    copyToClipboard(id) {
      navigator.clipboard.writeText(id);
      this.setNotification(true, "Skopiowano ID trasy.", id);
    },
    stringify(value) {
      return JSON.stringify(value, (key, val) => {
        if(val?.path) return val.path;
        if(val?.seconds !== undefined && val?.nanoseconds !== undefined) return { seconds: val.seconds, nanoseconds: val.nanoseconds };
        return val;
      }, 2);
    },
    formatRouteDate(route) {
      const start = route?.date?.start;
      const end = route?.date?.end;
      if(start?.asText && end?.asText) return `${start.asText} - ${end.asText}`;
      if(start?.seconds && end?.seconds) return `${new Date(start.seconds * 1000).toLocaleDateString("pl-PL")} - ${new Date(end.seconds * 1000).toLocaleDateString("pl-PL")}`;
      return "Brak daty";
    },
    formatCreatedDate(route) {
      const createdDate = route?.meta?.createdDate;
      if(createdDate?.seconds) return new Date(createdDate.seconds * 1000).toLocaleString("pl-PL");
      if(createdDate instanceof Date) return createdDate.toLocaleString("pl-PL");
      return "brak";
    },
    getRouteDateUnix(route) {
      return route?.date?.start?.seconds || route?.date?.start?.unix || route?.meta?.createdDate?.seconds || 0;
    },
    customSizeOfRoutes(count) {
      if(count === 1) return "trasa";
      if(count > 1 && count < 5) return "trasy";
      return "tras";
    },
    statusBadgeClass(statusId) {
      if(statusId === 1) return "bg-yellow-100 text-yellow-800";
      if(statusId === 2) return "bg-blue-100 text-blue-800";
      if(statusId === 3) return "bg-green-100 text-green-800";
      return "bg-gray-100 text-gray-700";
    },
  },
}
</script>
